import React, { useEffect, useMemo, useState } from "react";
import { Gamepad2, Gift, Grid2X2, Store, Users, Wrench } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import iconCraft from "../assets/icons/craft.svg";
import iconGames from "../assets/icons/games.svg";
import iconHold from "../assets/icons/hold.svg";
import iconHome from "../assets/icons/home.svg";
import iconWallet from "../assets/icons/wallet.svg";
import {
  BOTTOM_NAV_SECONDARY_GROUPS,
  getBottomNavActiveId,
  getBottomNavSecondaryId,
} from "../utils/bottomNavRoutes";

const PRIMARY_ITEMS = [
  { id: "home", path: "/home", icon: iconHome },
  { id: "claim", path: "/claim", Icon: Gift },
  { id: "leaderboard", path: "/leaderboard", icon: iconGames },
  { id: "wallet", path: "/wallet", icon: iconWallet },
  { id: "more", Icon: Grid2X2 },
];

const SECONDARY_ICONS = {
  referral: Users,
  staking: iconHold,
  craft: iconCraft,
  market: Store,
  crafting: iconCraft,
  mechanics: Wrench,
  games: Gamepad2,
};

function NavIcon({ item, size = 22 }) {
  if (item.Icon) {
    return <item.Icon className="bottom-nav__icon" size={size} strokeWidth={2.1} aria-hidden="true" />;
  }
  return <img className="bottom-nav__icon" src={item.icon} alt="" aria-hidden="true" draggable={false} />;
}

export default function BottomNav({ showReferral = false, isAdmin = false }) {
  const { t } = useTranslation("navigation");
  const location = useLocation();
  const navigate = useNavigate();
  const [moreOpen, setMoreOpen] = useState(false);
  const activeId = getBottomNavActiveId(location.pathname);
  const secondaryActiveId = getBottomNavSecondaryId(location.pathname);

  const secondaryItems = useMemo(
    () =>
      BOTTOM_NAV_SECONDARY_GROUPS.filter(
        (item) => !item.hidden && (!item.requiresPreviewAccess || showReferral)
      ),
    [showReferral]
  );

  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") setMoreOpen(false);
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [moreOpen]);

  const openRoute = (path) => {
    setMoreOpen(false);
    navigate(path);
  };

  return (
    <>
      {moreOpen && (
        <nav id="bottom-nav-more" className="bottom-nav-more" aria-label={t("secondaryAria")}>
          {secondaryItems.map((item) => {
            const active = secondaryActiveId === item.id;
            const disabled = Boolean(item.requiresAdminAccess && !isAdmin);
            const icon = SECONDARY_ICONS[item.id];
            const iconItem = typeof icon === "string" ? { icon } : { Icon: icon };
            const label = t(item.id);
            return (
              <button
                type="button"
                key={item.id}
                className={`bottom-nav-more__item${active ? " is-active" : ""}${disabled ? " is-disabled" : ""}`}
                aria-current={active ? "page" : undefined}
                aria-label={label}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                style={disabled ? { opacity: 0.45, filter: "grayscale(0.8)", cursor: "not-allowed" } : undefined}
                onClick={() => {
                  if (!disabled) openRoute(item.path);
                }}
              >
                <NavIcon item={iconItem} size={20} />
                <span>{label}</span>
              </button>
            );
          })}
        </nav>
      )}

      <nav className="bottom-nav" aria-label={t("primaryAria")}>
        {PRIMARY_ITEMS.map((item) => {
          const active = activeId === item.id;
          const isMore = item.id === "more";
          const label = t(item.id);
          return (
            <button
              type="button"
              key={item.id}
              className={`bottom-nav__item${active ? " is-active" : ""}`}
              aria-current={active ? (isMore ? "location" : "page") : undefined}
              aria-label={label}
              aria-expanded={isMore ? moreOpen : undefined}
              aria-controls={isMore ? "bottom-nav-more" : undefined}
              onClick={() => {
                if (isMore) {
                  setMoreOpen((value) => !value);
                  return;
                }
                openRoute(item.path);
              }}
            >
              <NavIcon item={item} />
              <span className="bottom-nav__label">{label}</span>
            </button>
          );
        })}
      </nav>
    </>
  );
}
